import { Box, Flex, HStack, Icon } from "@chakra-ui/react";
import CDS from "design-system";
import { TbClock, TbCalendar, TbSend } from "react-icons/tb";
import moment from "moment";
import { CHECK_TARGET_LABELS } from "features/Resources/Shared/constants";

interface Recipient {
  name: string;
  targetType: "MessageServiceChannel" | "User" | "OnboardingJourneyNetworkRelation";
  targetId: number;
  target: any;
}

interface CheckpointMetaCardProps {
  data: {
    evaluatedAt?: string;
    evaluateAt?: string;
    evaluateAtDescriptor?: string;
    checkTarget: string;
    deliveryMethod: string;
    recipients?: Recipient[];
  };
}

function recipientName(r: Recipient): string {
  if (r.targetType === "MessageServiceChannel") {
    const channelName = r.target?.name || r.target?.channelName;
    return channelName ? `#${channelName}` : "#channel";
  }
  if (r.targetType === "OnboardingJourneyNetworkRelation") {
    const roleName = r.target?.role || r.target?.name;
    if (roleName) return roleName.charAt(0).toUpperCase() + roleName.slice(1);
    return "Role";
  }
  return r.target?.fullName || r.target?.name || "Recipient";
}

const DELIVERY_LABEL: Record<string, string> = {
  message: "Slack",
  email: "Email",
};

function MetaRow({ icon, label, children }: { icon: any; label: string; children: React.ReactNode }) {
  return (
    <Flex
      align="center"
      px="4"
      py="2.5"
      gap="3"
      borderBottom="1px solid"
      borderColor="gray.100"
      _last={{ borderBottom: "none" }}
    >
      <Flex align="center" justify="center" w="28px" h="28px" borderRadius="md" bg="gray.50" flexShrink={0}>
        <Icon as={icon} boxSize="3.5" color="content.tertiary" />
      </Flex>
      <CDS.Text10 color="content.tertiary" minW="120px" flexShrink={0}>
        {label}
      </CDS.Text10>
      <Box flex={1} fontSize="sm" color="content.primary">
        {children}
      </Box>
    </Flex>
  );
}

function Pill({ children }: { children: React.ReactNode }) {
  return (
    <HStack
      as="span"
      display="inline-flex"
      spacing="1"
      bg="gray.50"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="full"
      px="2"
      py="0.5"
      fontSize="xs"
      color="content.secondary"
    >
      {children}
    </HStack>
  );
}

export default function CheckpointMetaCard({ data }: CheckpointMetaCardProps) {
  const isEvaluated = data.evaluatedAt != null;
  const checkTargetLabel = CHECK_TARGET_LABELS[data.checkTarget] || data.checkTarget;
  const deliveryLabel = DELIVERY_LABEL[data.deliveryMethod] || data.deliveryMethod || "Slack";
  const recipientNames = data.recipients?.map(recipientName).filter(Boolean) || [];
  const descriptor = data.evaluateAtDescriptor || "";

  const lastCheckedTime = isEvaluated && data.evaluatedAt ? moment(data.evaluatedAt).fromNow() : null;

  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="lg" bg="white" overflow="hidden">
      <MetaRow icon={TbClock} label="Last checked">
        {lastCheckedTime ? (
          <CDS.Text12 as="span">
            {lastCheckedTime}
            <CDS.Text10 as="span" color="content.tertiary">
              {" "}
              · {checkTargetLabel}
            </CDS.Text10>
          </CDS.Text12>
        ) : (
          <CDS.Text12 as="span" color="content.tertiary">
            Not run yet
          </CDS.Text12>
        )}
      </MetaRow>

      <MetaRow icon={TbCalendar} label="Originally scheduled">
        {data.evaluateAt ? (
          <CDS.Text12 as="span">
            {moment(data.evaluateAt).fromNow()}
            {descriptor && (
              <CDS.Text10 as="span" color="content.tertiary">
                {" "}
                · {descriptor}
              </CDS.Text10>
            )}
          </CDS.Text12>
        ) : (
          <CDS.Text12 as="span">{descriptor || "—"}</CDS.Text12>
        )}
      </MetaRow>

      <MetaRow icon={TbSend} label="Notifies">
        <HStack spacing="1.5" flexWrap="wrap" alignItems="center">
          {recipientNames.length > 0 ? (
            <>
              {recipientNames.map((name) => (
                <Pill key={name}>{name}</Pill>
              ))}
              <CDS.Text10 as="span" color="content.tertiary">
                via
              </CDS.Text10>
            </>
          ) : null}
          <Pill>{deliveryLabel}</Pill>
        </HStack>
      </MetaRow>
    </Box>
  );
}
